import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Power, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

import { asUTC } from '../../lib/ctTime';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatNextRun = (iso) => {
    if (!iso) return '—';
    return asUTC(iso).toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    }) + ' CT';
};

const describeRecurrence = (s) => {
    const t = `${String(s.hour_ct).padStart(2, '0')}:${String(s.minute_ct || 0).padStart(2, '0')} CT`;
    if (s.recurrence === 'daily') return `Every day at ${t}`;
    if (s.recurrence === 'weekly') return `Every ${DOW_LABELS[s.day_of_week ?? 0]} at ${t}`;
    if (s.recurrence === 'monthly') return `Every month on day ${s.day_of_month ?? 1} at ${t}`;
    return s.recurrence;
};

export const ScheduledPromos = ({ setConfirmAction, setConfirmDialogOpen }) => {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '', amount: '', reason: '',
        recurrence: 'daily', day_of_week: 6, day_of_month: 1,
        hour_ct: 10, minute_ct: 0,
    });

    const fetchSchedules = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/promo-schedules`, { withCredentials: true });
            setSchedules(data);
        } catch {
            toast.error('Failed to load schedules');
        }
        setLoading(false);
    };

    useEffect(() => { fetchSchedules(); }, []);

    const handleCreate = async () => {
        const amt = parseFloat(form.amount);
        if (!form.name.trim()) { toast.error('Schedule name is required'); return; }
        if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                amount: amt,
                reason: form.reason.trim() || 'Promotional credits',
                recurrence: form.recurrence,
                hour_ct: parseInt(form.hour_ct),
                minute_ct: parseInt(form.minute_ct) || 0,
            };
            if (form.recurrence === 'weekly') payload.day_of_week = parseInt(form.day_of_week);
            if (form.recurrence === 'monthly') payload.day_of_month = parseInt(form.day_of_month);
            await axios.post(`${API}/admin/promo-schedules`, payload, { withCredentials: true });
            toast.success(`Schedule "${payload.name}" created`);
            setCreateOpen(false);
            setForm({ name: '', amount: '', reason: '', recurrence: 'daily', day_of_week: 6, day_of_month: 1, hour_ct: 10, minute_ct: 0 });
            fetchSchedules();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create schedule');
        }
        setSaving(false);
    };

    const handleToggle = async (s) => {
        try {
            await axios.put(`${API}/admin/promo-schedules/${s.id}`, { active: !s.active }, { withCredentials: true });
            fetchSchedules();
        } catch {
            toast.error('Failed to toggle schedule');
        }
    };

    const handleDelete = (s) => {
        setConfirmAction({
            type: 'delete',
            message: `Delete the recurring promo "${s.name}"? This cannot be undone (it will simply stop firing).`,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API}/admin/promo-schedules/${s.id}`, { withCredentials: true });
                    toast.success('Schedule deleted');
                    fetchSchedules();
                } catch {
                    toast.error('Failed to delete schedule');
                }
                setConfirmDialogOpen(false);
                setConfirmAction(null);
            },
        });
        setConfirmDialogOpen(true);
    };

    return (
        <div className="card-dark p-6" data-testid="schedules-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#FFD700]" />
                    <h3 className="text-base font-bold text-white">Recurring / Scheduled Promos</h3>
                    <span className="text-[10px] text-[#A0AAB2] ml-1">({schedules.length})</span>
                </div>
                <Button size="sm" onClick={() => setCreateOpen(true)}
                    className="bg-[#FFD700] text-black font-bold h-8" data-testid="create-schedule-btn">
                    <Plus className="w-3 h-3 mr-1" /> New Schedule
                </Button>
            </div>

            {loading ? (
                <p className="text-[#A0AAB2] text-xs text-center py-3">Loading…</p>
            ) : schedules.length === 0 ? (
                <p className="text-[#A0AAB2] text-xs">
                    No recurring promos yet. Create one to auto-fire daily / weekly / monthly without admin intervention.
                </p>
            ) : (
                <div className="space-y-2">
                    {schedules.map(s => (
                        <div key={s.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${s.active ? 'bg-[#0a0a0a] border-[#FFD700]/30' : 'bg-[#0a0a0a]/50 border-white/10 opacity-60'}`}
                            data-testid={`schedule-row-${s.id}`}>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Switch checked={s.active} onCheckedChange={() => handleToggle(s)}
                                    data-testid={`schedule-toggle-${s.id}`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-bold truncate">
                                        {s.name}
                                        <span className="ml-2 text-[#FFD700] font-mono text-xs">${s.amount.toFixed(2)}</span>
                                    </p>
                                    <p className="text-[10px] text-[#A0AAB2] truncate">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {describeRecurrence(s)} · {(s.user_ids || []).length === 0 ? 'all active users' : `${s.user_ids.length} player(s)`}
                                    </p>
                                    <p className="text-[10px] text-[#666] truncate">
                                        Next: <span className="text-[#39FF14]">{formatNextRun(s.next_run_at)}</span>
                                        {s.last_run_at && <> · Last: {formatNextRun(s.last_run_at)} ({s.last_run_recipients} sent)</>}
                                    </p>
                                </div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(s)}
                                className="text-[#A0AAB2] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 w-8 p-0"
                                data-testid={`schedule-delete-${s.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Schedule Dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-[#FFD700]" /> New Recurring Promo
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Auto-credits ALL active users on schedule (Central Time).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Schedule Name</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder='e.g. "Daily login reward"' className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                data-testid="sched-name-input" autoFocus />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Amount per user</Label>
                                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                                    placeholder="e.g. 1.00" className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="sched-amount-input" />
                            </div>
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Reason</Label>
                                <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                                    placeholder="Promotional credits" className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="sched-reason-input" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Recurrence</Label>
                            <div className="grid grid-cols-3 gap-2 mt-1">
                                {['daily', 'weekly', 'monthly'].map(r => (
                                    <button type="button" key={r}
                                        onClick={() => setForm({ ...form, recurrence: r })}
                                        className={`py-2 rounded-lg text-xs font-bold uppercase border transition-colors ${
                                            form.recurrence === r
                                                ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                                        }`}
                                        data-testid={`sched-rec-${r}`}>
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {form.recurrence === 'weekly' && (
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Day of week</Label>
                                <div className="grid grid-cols-7 gap-1 mt-1">
                                    {DOW_LABELS.map((label, idx) => (
                                        <button type="button" key={idx}
                                            onClick={() => setForm({ ...form, day_of_week: idx })}
                                            className={`py-2 rounded-lg text-[10px] font-bold border ${
                                                form.day_of_week === idx
                                                    ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                    : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                                            }`}
                                            data-testid={`sched-dow-${idx}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {form.recurrence === 'monthly' && (
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Day of month (1–28)</Label>
                                <Input type="number" min={1} max={28} value={form.day_of_month}
                                    onChange={e => setForm({ ...form, day_of_month: e.target.value })}
                                    className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="sched-dom-input" />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Hour (CT, 0–23)</Label>
                                <Input type="number" min={0} max={23} value={form.hour_ct}
                                    onChange={e => setForm({ ...form, hour_ct: e.target.value })}
                                    className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="sched-hour-input" />
                            </div>
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Minute (CT)</Label>
                                <Input type="number" min={0} max={59} value={form.minute_ct}
                                    onChange={e => setForm({ ...form, minute_ct: e.target.value })}
                                    className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="sched-minute-input" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}
                            className="border-white/20 text-white">Cancel</Button>
                        <Button onClick={handleCreate} disabled={saving || !form.name.trim() || !form.amount}
                            className="bg-[#FFD700] text-black font-bold" data-testid="sched-save-btn">
                            {saving ? 'Saving…' : <><Power className="w-3 h-3 mr-1.5" />Activate Schedule</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
